import { ErrorResponseBody } from '../state/shared/apiTypes';

export const isOkHttpResponse = <
  TResponse extends Response,
  TOkResponseBody = Response
>(
  objectToBeChecked: unknown,
  _objectPropertyToBeChecked: unknown = objectToBeChecked
): _objectPropertyToBeChecked is TOkResponseBody =>
  (objectToBeChecked as TResponse).ok;

export const isErrorHttpResponse = <
  TResponse extends HttpResponse<ErrorResponseBody>,
  TOkResponseBody
>(
  objectToBeChecked: unknown,
  _objectPropertyToBeChecked: unknown
): _objectPropertyToBeChecked is TOkResponseBody =>
  !!(
    !(objectToBeChecked as TResponse).ok &&
    (objectToBeChecked as TResponse).parsedBody?.errors
  );

export type HttpResponse<TResponseBody = null> = TResponseBody extends null
  ? Response | (Response & { parsedBody: ErrorResponseBody })
  : Response & {
      parsedBody?: TResponseBody | ErrorResponseBody;
    };

async function http<TResponseBody = null>(
  request: RequestInfo
): Promise<HttpResponse<TResponseBody>> {
  const response = await fetch(request);
  let data: unknown;
  try {
    data = await response.text(); // Parse as text
    data = JSON.parse(data as string); // Try to parse it as json
  } catch (err) {
    /* suppress error (return a valid Response object outside of try-catch,
       caller needs to check for parsedBody and handle errors) */
  }
  (response as HttpResponse<unknown>).parsedBody = data;
  return response;
}

async function get<TResponseBody>(
  path: string,
  args: RequestInit = { method: 'get' }
): Promise<HttpResponse<TResponseBody>> {
  return http<TResponseBody>(new Request(path, args));
}

async function post<TRequestBody, TResponseBody>(
  path: string,
  body: TRequestBody,
  args: Omit<RequestInit, 'method' | 'body'> = {
    headers: { 'Content-Type': 'application/json' },
  }
): Promise<HttpResponse<TResponseBody>> {
  return http<TResponseBody>(
    new Request(path, { ...args, method: 'post', body: JSON.stringify(body) })
  );
}

async function put<TRequestBody, TResponseBody>(
  path: string,
  body: TRequestBody,
  args: Omit<RequestInit, 'method' | 'body'> = {
    headers: { 'Content-Type': 'application/json' },
  }
): Promise<HttpResponse<TResponseBody>> {
  return http<TResponseBody>(
    new Request(path, { ...args, method: 'put', body: JSON.stringify(body) })
  );
}

// delete & patch not implemented b/c there are no use cases for those actions in the login/register app

const fetchHelper = {
  get,
  post,
  put,
};

export type ApiService = typeof fetchHelper;

export default fetchHelper;
